import type { ColDef, ValueFormatterParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { DeviceType } from '~/types/device-type'
import type { IIap } from '~/types/iap'

import ActionMenu from '~/components/ActionMenu'
import IapSubTypeCellRenderer from '~/components/CellRenderers/IAPSubTypeCellRenderer'
import { FormatTextCellRenderer } from '~/components/CellRenderers/TextCellRenderer'
import WithSubTextCellRenderer from '~/components/CellRenderers/WithSubTextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { formatDuration } from '~/utils/date'
import { flattenFeatures } from '~/utils/iap'
import { copyText } from '~/utils/navigator'
import { getNumberWithSpaces } from '~/utils/number'
import { actionsColDef, iapStatusColDef, withSubTextColDef } from '~/utils/table'

const ActionsCellRenderer = ({ data }: ICellRendererParams<IIap>): ReactElement => {
  if (data) {
    let menu: Menu = {
      actions: [
        {
          action: () => copyText(data.sku, 'Sku copied to clipboard.'),
          label: 'Copy sku',
        },
      ],
    }

    if (data && data.device_type === 'ios') {
      menu = {
        ...menu,
        navigations: [
          {
            action: () =>
              window.open(
                `https://appstoreconnect.apple.com/apps/${data.app_store_id}/appstore/subscriptions/${data.store_id}`,
                '_blank'
              ),
            label: 'View Subscription on AppStoreConnect',
            type: 'external',
          },
          {
            action: () =>
              window.open(
                `https://appstoreconnect.apple.com/apps/${data.app_store_id}/appstore/subscription-groups/${data.subscription_group_id}`,
                '_blank'
              ),
            label: 'View Group on AppStoreConnect',
            type: 'external',
          },
        ],
      }
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const getColumns = (deviceType: DeviceType): ColDef[] => {
  const columns: ColDef[] = [
    withSubTextColDef(WithSubTextCellRenderer, {
      field: 'sku',
      headerComponentParams: {
        menuIcon: 'fa-bars',
        template: sharedHeaderTemplate({
          headerSubtitle: 'Name',
          headerSubtitleClassname: 'capitalized',
          headerTitle: 'In-App Purchase ID',
        }),
      },
      headerName: '',
      headerTooltip: 'In-App Purchase ID',
      sortable: false,
      valueGetter: (params: ValueGetterParams<IIap>): any => ({
        subText: params.data?.sku,
        text: params.data?.title,
      }),
    }),
    {
      cellRenderer: IapSubTypeCellRenderer,
      field: 'sub_type',
      filter: true,
      headerName: 'Type',
      headerTooltip: 'Type',
      sortable: false,
      suppressHeaderMenuButton: true,
      suppressSizeToFit: true,
      width: 120,
    },
    iapStatusColDef({
      field: 'status',
      headerTooltip: 'Status',
      sortable: false,
    }),
    {
      cellRenderer: FormatTextCellRenderer,
      field: 'price',
      headerName: 'Price',
      headerTooltip: 'Price',
      maxWidth: 130,
      minWidth: 90,
      sortable: false,
      suppressHeaderMenuButton: true,
      valueFormatter: (params: ValueFormatterParams<IIap>): string => {
        return params.value ? `$ ${getNumberWithSpaces(params.value)}` : '--'
      },
    },
    {
      cellRenderer: FormatTextCellRenderer,
      field: 'trial_period_days',
      headerName: 'Free trial period',
      headerTooltip: 'Free trial period',
      minWidth: 100,
      sortable: false,
      suppressHeaderMenuButton: true,
      valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDuration(params.value, 'd') : '--'),
      width: 140,
    },
    {
      cellRenderer: FormatTextCellRenderer,
      field: 'subscription_period_days',
      headerName: 'Subscription period',
      headerTooltip: 'Subscription period',
      minWidth: 100,
      sortable: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDuration(params.value, 'd') : '--'),
      width: 140,
    },
    {
      field: 'unlockables',
      headerName: 'Unlockables',
      headerTooltip: 'Unlockables',
      minWidth: 120,
      sortable: false,
      valueFormatter: ({ value }: ValueFormatterParams<IIap, IIap['unlockables']>): string =>
        value ? `${flattenFeatures(value).length.toString()} features` : '',
      width: 140,
    },
    actionsColDef(ActionsCellRenderer, {
      field: 'actions',
    }),
  ]

  if (deviceType === 'ios') {
    columns.push(
      withSubTextColDef(WithSubTextCellRenderer, {
        field: 'subscription_group_name',
        headerComponentParams: {
          menuIcon: 'fa-bars',
          template: sharedHeaderTemplate({
            headerSubtitle: 'ID',
            headerSubtitleClassname: 'capitalized',
            headerTitle: 'Group',
          }),
        },
        headerName: '',
        headerTooltip: 'Group',
        minWidth: 200,
        sortable: false,
        valueGetter: (params: ValueGetterParams<IIap>): any => ({
          subText: params.data?.subscription_group_id,
          text: params.data?.subscription_group_name,
        }),
      })
    )
  }

  return columns
}

export default getColumns
